type ErrorType = 'client' | 'network';

export default function determineUnknownErrorType(error: unknown): ErrorType {
  // Check for offline status first
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'network';
  }

  if (error instanceof Error) {
    const errorMessage = (error.message || '').toLowerCase();
    const errorName = (error.name || '').toLowerCase();

    // We do an extra check for Safari 17+ as it has a very generic error message.
    // Network errors in Safari have no stack.
    if (errorMessage === 'load failed' && error.stack === undefined) {
      return 'network';
    }

    // Common network error messages from various browsers and fetch implementations.
    const networkErrorMessages = [
      'failed to fetch', // Chrome
      'network request failed', // `cross-fetch`
      'network error', // Chrome
      'networkerror when attempting to fetch resource.', // Firefox
      'the internet connection appears to be offline.', // Safari 16
    ];

    if (networkErrorMessages.some((msg) => errorMessage.includes(msg))) {
      return 'network';
    }

    // This can happen if the request is aborted, e.g. by a timeout
    if (errorName === 'aborterror') {
      return 'network';
    }

    // `statusCode` is added by certain fetch implementations.
    // We can use it in our own fetch wrappers.
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      // A status of 0 can indicate a blocked request (e.g., CORS) or other client-side network failures.
      if (error.statusCode === 0) {
        return 'network';
      }
      // 5xx status codes are network errors.
      if (error.statusCode >= 500 && error.statusCode <= 599) {
        return 'network';
      }
    }
  }

  // Every other error that's hard to determine should be a client error of some sort
  return 'client';
}
